import { SetStateAction, useRef } from "react";

/**
 * Stale flags for layers whose source is rebuilt on every render. Map pushes
 * a layer's source to the map (setData, or a rebuild when its styleKey
 * moved) only while the layer is stale, then clears the flag. Here the flag
 * means "this key has not reached the map yet": a layer that always said
 * stale had its whole source (thousands of findings) re-sent on every render
 * of the project page.
 *
 * Returns a function giving one layer's `stale` / `setStale`; `key` lists
 * what the source and style are built from, compared by identity, so pass
 * memoised values.
 */
export function useLayerStale() {
  const applied = useRef<Record<string, unknown[]>>({});
  return (id: string, ...key: unknown[]) => {
    const last = applied.current[id];
    const stale = !last || last.length !== key.length || last.some((v, i) => v !== key[i]);
    const setStale = (next: SetStateAction<boolean>) => {
      const value = typeof next === "function" ? next(stale) : next;
      if (value) delete applied.current[id];
      else applied.current[id] = key;
    };
    return { stale, setStale };
  };
}
